import { defineStore } from 'pinia'
import api from '@/api'
import type { UpcomingDraw } from '@/types/lottery'
import { useAuthStore } from '@/stores/auth'
import { iframeCommunication } from '@/utils/iframeCommunication'

export interface UpcomingDrawsPagination {
  total_count: number
  total_pages: number
  current_page: number
  limit: number
}

function readAccountFromUrl(): string {
  try {
    const value = new URLSearchParams(window.location.search).get('account')
    return value ? value.split('?')[0].trim() : ''
  } catch {
    return ''
  }
}

/** Resolve tenant account before draw APIs so cutoff overrides are always applied. */
function resolveAccountSlug(): string {
  return useAuthStore().resolveTenantAccount() || ''
}

/** True when URL/session/iframe handshake suggests a tenant account will arrive soon. */
function accountContextExpected(): boolean {
  if (readAccountFromUrl()) {
    return true
  }
  if (typeof sessionStorage !== 'undefined') {
    if (sessionStorage.getItem('auth_account')) {
      return true
    }
    if (sessionStorage.getItem('auth_token')) {
      return true
    }
  }
  if (iframeCommunication.isRunningInIframe() && !iframeCommunication.hasReceivedParentReady()) {
    return true
  }
  return false
}

function shouldDeferAccountScopedFetch(accountSlug: string): boolean {
  if (accountSlug !== '') {
    return false
  }
  if (!accountContextExpected()) {
    return false
  }
  return deferAccountFetchAttempts < MAX_DEFER_ACCOUNT_FETCH_ATTEMPTS
}

function isStaleAccountScopedResponse(requestAccount: string): boolean {
  return requestAccount !== resolveAccountSlug()
}

let fetchDrawsRequestId = 0
let fetchAllProductsRequestId = 0
let deferAccountFetchTimer: ReturnType<typeof setTimeout> | null = null
let deferAccountFetchAttempts = 0
const MAX_DEFER_ACCOUNT_FETCH_ATTEMPTS = 12

function scheduleDeferredAccountFetch(retry: () => void) {
  if (deferAccountFetchTimer) {
    return
  }
  deferAccountFetchAttempts += 1
  deferAccountFetchTimer = window.setTimeout(() => {
    deferAccountFetchTimer = null
    retry()
  }, 400)
}

export const useUpcomingDrawsStore = defineStore('upcomingDraws', {
  state: () => ({
    draws: [] as UpcomingDraw[],
    loading: false,
    error: null as string | null,
    /** Account slug used for the current in-memory draw rows (cutoff overrides are account-specific). */
    loadedAccountSlug: null as string | null,
    pagination: {
      total_count: 0,
      total_pages: 1,
      current_page: 1,
      limit: 500,
    } as UpcomingDrawsPagination,
  }),
  actions: {
    invalidateAccountContext() {
      this.loadedAccountSlug = null
    },

    async fetchDraws(page = 1, drawDate?: string, force = false) {
      const accountSlug = resolveAccountSlug()

      if (!force && shouldDeferAccountScopedFetch(accountSlug)) {
        scheduleDeferredAccountFetch(() => {
          void this.fetchDraws(page, drawDate, force)
        })
        return
      }

      if (
        !force &&
        page === 1 &&
        this.draws.length > 0 &&
        this.loadedAccountSlug === accountSlug
      ) {
        return
      }

      if (!force && !accountSlug && this.loadedAccountSlug) {
        return
      }

      const requestId = ++fetchDrawsRequestId
      const requestAccount = accountSlug

      this.loading = true
      this.error = null
      try {
        const payload: Record<string, string | number> = {
          page,
          limit: this.pagination.limit,
        }
        if (drawDate) {
          payload.drawDate = drawDate
        }
        if (accountSlug) {
          payload.account = accountSlug
        }

        const { data } = await api.post('/upcoming-draws', payload)

        let drawsArr: UpcomingDraw[] = []
        let paginator = { ...this.pagination }
        if (data && data.result && data.result.result) {
          drawsArr = data.result.result
          if (data.result.paginator) {
            paginator = { ...paginator, ...data.result.paginator }
          }
        }
        if (requestId !== fetchDrawsRequestId) {
          return
        }
        if (isStaleAccountScopedResponse(requestAccount)) {
          return
        }

        if (page === 1) {
          this.draws = drawsArr
          this.loadedAccountSlug = requestAccount
        } else {
          this.draws = [...this.draws, ...drawsArr]
        }
        this.pagination = paginator
      } catch (e: unknown) {
        console.error('Error fetching upcoming draws:', e)
        this.error = (e as { userMessage?: string; message?: string })?.userMessage ||
                     (e as { userMessage?: string; message?: string })?.message ||
                     'Failed to fetch upcoming draws'
      } finally {
        this.loading = false
      }
    },

    async fetchAllProducts(page = 1, force = false) {
      const accountSlug = resolveAccountSlug()

      if (!force && shouldDeferAccountScopedFetch(accountSlug)) {
        scheduleDeferredAccountFetch(() => {
          void this.fetchAllProducts(page, force)
        })
        return
      }

      if (
        !force &&
        page === 1 &&
        this.draws.length > 0 &&
        this.loadedAccountSlug === accountSlug
      ) {
        return
      }

      if (!force && !accountSlug && this.loadedAccountSlug) {
        return
      }

      const requestId = ++fetchAllProductsRequestId
      const requestAccount = accountSlug

      this.loading = true
      this.error = null
      try {
        const payload: Record<string, string | number> = {
          page,
          limit: this.pagination.limit,
        }
        if (accountSlug) {
          payload.account = accountSlug
        }

        const { data } = await api.post('/products', payload)

        let drawsArr: UpcomingDraw[] = []
        let paginator = { ...this.pagination }
        if (data && data.result && data.result.result) {
          drawsArr = data.result.result
          if (data.result.paginator) {
            paginator = { ...paginator, ...data.result.paginator }
          }
        }
        if (requestId !== fetchAllProductsRequestId) {
          return
        }
        if (isStaleAccountScopedResponse(requestAccount)) {
          return
        }

        if (page === 1) {
          this.draws = drawsArr
          this.loadedAccountSlug = requestAccount
        } else {
          this.draws = [...this.draws, ...drawsArr]
        }
        this.pagination = paginator
      } catch (e: unknown) {
        console.error('Error fetching all products:', e)
        this.error = (e as { userMessage?: string; message?: string })?.userMessage ||
                     (e as { userMessage?: string; message?: string })?.message ||
                     'Failed to fetch products'
      } finally {
        this.loading = false
      }
    },

    setPage(page: number) {
      this.pagination.current_page = page
      this.fetchDraws(page)
    },
  },
})
